import { useEffect, useRef, useState } from 'react';
import type { default as LottieComponent } from 'lottie-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePrefersReducedMotion } from '../use-prefers-reduced-motion';
import { useInView } from '../use-in-view';

type LottieData = Record<string, unknown>;

/**
 * Client-only Lottie player. The library (`lottie-react` → `lottie-web`) touches
 * `window`/`document`, so it is loaded lazily after mount to stay SSR-safe. When the
 * user prefers reduced motion the animation is rendered paused on its first frame.
 */
export function LottiePlayer({
	animationData,
	className,
	loop = true,
	playWhenVisible = false,
	fit = 'contain',
	ariaLabel,
}: {
	animationData: LottieData;
	className?: string;
	loop?: boolean;
	/**
	 * Hold the animation at its reserved box until it scrolls into view, then start it. Only worth
	 * setting on a `loop={false}` player: a one-shot that plays while it is still two screens down is
	 * over before anyone sees it, and the reader meets a still frame. `useInView` latches, so it plays
	 * once and stays played — scrolling back up does not rewind it.
	 */
	playWhenVisible?: boolean;
	/**
	 * How the artboard sits in the box it is given. `contain` letterboxes it, which is right for a
	 * drawing that has to be seen whole. `cover` fills the box and crops, anchored to the TOP so what
	 * gets cut is the bottom of the drawing rather than whatever the animation is doing above it.
	 */
	fit?: 'contain' | 'cover';
	ariaLabel?: string;
}) {
	const reduce = usePrefersReducedMotion();
	const boxRef = useRef<HTMLDivElement>(null);
	const inView = useInView(boxRef);
	const [Lottie, setLottie] = useState<typeof LottieComponent | null>(null);

	useEffect(() => {
		let alive = true;
		import('lottie-react').then((m) => {
			// `lottie-react` is CJS: Vite's interop can nest the component one level
			// deeper (`m.default.default`), so unwrap whichever shape we get.
			const d = m.default as typeof LottieComponent & { default?: typeof LottieComponent };
			if (alive) setLottie(() => d.default ?? d);
		});
		return () => {
			alive = false;
		};
	}, []);

	// Reserve the box so layout doesn't jump before the player hydrates — which only holds if the
	// CALLER's box does not itself depend on the player. An `h-full` inside an auto-height parent
	// measures zero here and the artboard's own height once the SVG lands; see `CoinsTile`.
	if (!Lottie || (playWhenVisible && !inView))
		return <div ref={boxRef} className={className} aria-label={ariaLabel} role="img" />;

	// Reduced motion gets a still — of the LAST frame, not the first. These animations are build-ons:
	// every layer starts at zero opacity and half scale, so holding frame 0 renders an EMPTY box. The
	// segment is what pins it there without needing a ref to call `goToAndStop` on.
	const end = typeof animationData.op === 'number' ? animationData.op : undefined;

	return (
		<Lottie
			animationData={animationData}
			loop={reduce ? false : loop}
			autoplay={!reduce}
			initialSegment={reduce && end ? [Math.max(0, end - 1), end] : undefined}
			className={cn(className)}
			aria-label={ariaLabel}
			role="img"
			rendererSettings={{ preserveAspectRatio: fit === 'cover' ? 'xMidYMin slice' : 'xMidYMid meet' }}
		/>
	);
}
